'use client';
import React from 'react';
import classNames from 'classnames';
import styles from './NewArticleLayoutMain.module.scss';

import {
  ArticleBlock,
  ArticleData,
  Campaigns,
  CampaignStrategy,
  getCanonicalUrl,
  getPrimaryTickers,
  setDFPTargeting,
} from '@benzinga/article-manager';

import { ShareButtons } from '@benzinga/ui/share-buttons';
import { appEnvironment, runningClientSide } from '@benzinga/utils';

import { ButtonVariant, ErrorBoundary } from '@benzinga/core-ui';
import { AnalyticsContext, VisiblePageTracker } from '@benzinga/analytics';

import { NewCampaign } from './NewCampaign';
import { ArticleIFrame } from './ArticleIFrame';
import { ArticleBlocks } from './ArticleBlocks';
import { ArticleHeadlineContent } from './ArticleHeadlineContent';
import { ArticleBodyContent } from './ArticleBody';
import { PostedIn } from './PostedIn';

import { ArticleFeaturedTicker, ArticlePageProps } from '../entities';
import { IsUserPaywalledReturn } from '@benzinga/user-context';
import { ArticleContentWrapper } from './ArticleLayoutChrome';
import { VideoArticleBody } from './VideoArticleBody';
import RelatedArticles from './RelatedArticles';

const PaywallSurface = React.lazy(() =>
  import('@benzinga/auth-ui/paywall').then(module => ({ default: module.PaywallSurface })),
);
const RegwallSurface = React.lazy(() =>
  import('@benzinga/auth-ui/regwall').then(module => ({ default: module.RegwallSurface })),
);

const ArticleImageGallery = React.lazy(() =>
  import('./ArticleImageGallery').then(module => ({
    default: module.ArticleImageGallery,
  })),
);
const ArticleFeaturedTickersList = React.lazy(() => import('./ArticleFeaturedTickersList'));

export interface NewArticleLayoutMainProps extends Partial<ArticlePageProps> {
  articleData: ArticleData;
  articleScrollViewMoreLink?: string;
  baseUrl?: string;
  disablePartnerAdOnScroll?: boolean;
  hasAdLight: boolean;
  hideTopPanel?: boolean;
  isEditor: boolean;
  isBot: boolean;
  generatedByAi?: boolean;
  googleNewsUrlKey?: 'benzinga' | 'benzingaIndia';
  loadMoreButtonVariant?: ButtonVariant;
  isTaggedPressRelease?: boolean;
  isDraft?: boolean;
  relatedArticles?: ArticleData[];
  showAdvertiserDisclosure?: boolean;
  showApiText?: boolean;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  executeReplaceHistory?: boolean;
  executePageviewEvent?: boolean;
  showPartnerAd?: boolean;
  campaign?: Campaigns;
  campaignStrategy?: CampaignStrategy;
  campaignTicker?: string;
  trackMeta?: boolean;
  trackPageView?: boolean;
  primis?: boolean;
  disableCampaign?: boolean;
  raptiveEnabled?: boolean;
  isNotPaywalled?: boolean;
  isPaywallActive?: boolean;
  paywall: IsUserPaywalledReturn;
  featuredTickers?: ArticleFeaturedTicker[];
  isVideoArticle?: boolean;
  paywallStyle?: IsUserPaywalledReturn['paywallStyle'];
}

const copyright = `© ${new Date().getFullYear()} Benzinga.com. Benzinga does not provide investment advice. All rights reserved.`;

const GooglePreferredSource = () => {
  return (
    <p className="block core-block">
      To add <strong>Benzinga News</strong> as your preferred source on Google,{' '}
      <a
        href={`https://google.com/preferences/source?q=${encodeURIComponent(appEnvironment().config().url)}`}
        rel="noreferrer"
        target="_blank"
      >
        click here
      </a>
      .
    </p>
  );
};

const MemoCampaign = React.memo(NewCampaign);

export const NewArticleLayoutMain: React.FC<NewArticleLayoutMainProps> = React.memo(
  ({
    articleData,
    //articleIndex,
    campaignSettings = {},
    campaignStrategy,
    campaignTicker,
    deviceType,
    executePageviewEvent = false,
    executeReplaceHistory = false,
    featuredTickers,
    //followUpQuestions,
    generatedByAi = false,
    hasAdLight = false,
    isBot = true,
    isPaywallActive,
    isTemplate = false,
    isVideoArticle = false,
    layout,
    paywallStyle = 'paywall',
    postedInVariant = 'default',
    raptiveEnabled = false,
    relatedArticles,
    showAdvertiserDisclosure = false,
    showApiText = true,
    showFontAwesomeIcons = false,
    showWhatsAppIcon = false,
    trackMeta = true,
    trackPageView = true,
    wordCount,
  }) => {
    const channels = articleData.channels ? articleData.channels : [];
    const tags = articleData.tags ? articleData.tags : [];
    const title = articleData.title;

    React.useEffect(() => {
      if (isTemplate || !isPaywallActive || !runningClientSide()) return;
      const prefetchPaywall = () => {
        if (paywallStyle === 'regwall') {
          void import('@benzinga/auth-ui/regwall');
          return;
        }
        void import('@benzinga/auth-ui/paywall');
      };
      if (typeof window.requestIdleCallback === 'function') {
        const idleId = window.requestIdleCallback(prefetchPaywall, { timeout: 4000 });
        return () => window.cancelIdleCallback(idleId);
      }
      const timeoutId = globalThis.setTimeout(prefetchPaywall, 2000);
      return () => globalThis.clearTimeout(timeoutId);
    }, [isTemplate, isPaywallActive, paywallStyle]);

    const onCampaignsRendered = () => {
      // renderTickersBoxes('#article-body', true);
    };

    const generateContentWithCampaign = (blocks: ArticleBlock[]) => <ArticleBlocks blocks={blocks} />;

    const articleUrl = getCanonicalUrl(articleData);
    const tickers = getPrimaryTickers(articleData?.tickers);
    const ArticleAuthSurface = paywallStyle === 'regwall' ? RegwallSurface : PaywallSurface;

    const onArticleVisible = articleData => {
      setDFPTargeting(articleData);
    };

    const slideshows = articleData?.meta?.wp_post_meta?.Slideshow;

    return (
      <ArticleContentWrapper
        className={classNames('article-content-body', { 'is-headline': articleData?.isHeadline })}
        id={`node-${articleData.nodeId}`}
      >
        {Array.isArray(slideshows) && slideshows.length > 0 && (
          <ErrorBoundary name="article-layout-main-picture-block">
            <React.Suspense fallback={<div aria-hidden className="h-[440px] w-full" />}>
              <ArticleImageGallery slideshows={slideshows} />
            </React.Suspense>
          </ErrorBoundary>
        )}
        <AnalyticsContext.Provider value={{ label: 'Article Above Content' }}>
          {layout && Array.isArray(layout?.content_header?.blocks) && (
            <ArticleBlocks blocks={layout.content_header.blocks} />
          )}
        </AnalyticsContext.Provider>
        {trackPageView && (
          <ErrorBoundary name="article-layout-main-visible-page-tracker">
            <VisiblePageTracker
              article={articleData}
              executeImpressionEvent={false}
              executePageviewEvent={executePageviewEvent}
              executeReplaceHistory={executeReplaceHistory}
              meta={articleData.metaProps}
              onImpress={() => onArticleVisible(articleData)}
              trackMeta={trackMeta}
            >
              <div style={{ height: 2 }} />
            </VisiblePageTracker>
          </ErrorBoundary>
        )}

        {articleData?.isHeadline ? (
          <ArticleHeadlineContent />
        ) : (
          <>
            {isPaywallActive && !isTemplate && (
              <ErrorBoundary fallback={<div aria-hidden className="min-h-[280px] w-full" />} name="article-paywall">
                <React.Suspense fallback={<div aria-hidden className="min-h-[280px] w-full" />}>
                  <ArticleAuthSurface placement="article" surface={paywallStyle} />
                </React.Suspense>
              </ErrorBoundary>
            )}
            <AnalyticsContext.Provider value={{ label: 'Article In Content' }}>
              <ArticleBodyContent
                isBot={isBot}
                isPaywalled={isPaywallActive && !isTemplate}
                isSponsored={showAdvertiserDisclosure}
              >
                {isTemplate ? null : (
                  <>
                    {isVideoArticle ? (
                      <VideoArticleBody articleData={articleData} />
                    ) : articleData.shouldDisplayedInFrame ? (
                      <React.Suspense fallback={<div />}>
                        <ErrorBoundary name="article-layout-main-article-iframe">
                          <ArticleIFrame articleBody={articleData.body} />
                        </ErrorBoundary>
                      </React.Suspense>
                    ) : (
                      <ErrorBoundary disableFallback={true} name="article-layout-main-campaign">
                        <MemoCampaign
                          {...(campaignSettings || {})}
                          articleBlocks={Array.isArray(articleData?.blocks) ? articleData.blocks : []}
                          articleBody={Array.isArray(articleData?.parsedBody) ? articleData.parsedBody : []}
                          articleUrl={articleUrl}
                          channels={channels}
                          contentType={articleData.type}
                          createdDate={articleData.createdAt}
                          deviceType={deviceType}
                          hasAdLight={hasAdLight}
                          isSponsored={showAdvertiserDisclosure}
                          layout={layout}
                          nodeId={articleData.nodeId}
                          onRendered={onCampaignsRendered}
                          raptiveEnabled={raptiveEnabled}
                          strategy={campaignStrategy}
                          tags={tags}
                          ticker={campaignTicker}
                          wordCount={wordCount}
                        >
                          {generateContentWithCampaign}
                        </MemoCampaign>
                      </ErrorBoundary>
                    )}

                    {Array.isArray(featuredTickers) && featuredTickers.length > 0 && (
                      <div className="flex md:hidden flex-col gap-4 w-full max-w-[450px] my-4 mx-auto">
                        <React.Suspense fallback={<div aria-hidden className="min-h-[280px] w-full" />}>
                          <ArticleFeaturedTickersList tickers={featuredTickers} />
                        </React.Suspense>
                      </div>
                    )}

                    {generatedByAi && (
                      <p className="block core-block">
                        <em>
                          This content was partially produced with the help of AI tools and was reviewed and published
                          by Benzinga editors.
                        </em>
                      </p>
                    )}

                    {showApiText && (
                      <a
                        className="text-sm my-4"
                        href="https://www.benzinga.com/apis?utm_source=benzinga.com&amp;utm_campaign=article-bottom"
                        rel="noreferrer"
                        target="_blank"
                      >
                        Market News and Data brought to you by Benzinga APIs
                      </a>
                    )}
                    <p className="copyright">{copyright}</p>
                    <GooglePreferredSource />
                  </>
                )}
              </ArticleBodyContent>
            </AnalyticsContext.Provider>
          </>
        )}

        {/* This should be moved to be in the article for longer articles */}
        {/* {checkIfShouldLoadCalendar(articleData) && <ArticleCalendarWidget article={articleData} />} */}

        {title && (
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <ShareButtons
              buttonLocation="article_page"
              buttonStyle={{
                bgStyle: {
                  fill: '#3F83F81A',
                },
                iconFillColor: '#3F83F8',
              }}
              className="flex"
              showFontAwesomeIcons={showFontAwesomeIcons}
              showWhatsAppIcon={showWhatsAppIcon}
              tickers={tickers}
              title={title}
              url={articleUrl}
              utmSource="articleShare"
            />
          </div>
        )}

        <hr className="mb-2" />

        {trackPageView && (
          <ErrorBoundary name="article-layout-main-visible-page-tracker">
            <VisiblePageTracker
              article={articleData}
              executeImpressionEvent={false}
              executePageviewEvent={false}
              meta={articleData.metaProps}
              trackMeta={trackMeta}
            >
              <PostedIn terms={articleData.terms ?? []} variant={postedInVariant} />
            </VisiblePageTracker>
          </ErrorBoundary>
        )}

        {/* {raptiveEnabled && (
            <React.Suspense fallback={<div className="h-[90px] w-[300px] mb-2" />}>
              <FreestarAd
                className="flex items-center justify-center mb-4 min-h-[50px]"
                onlyDesktop={true}
                placement="belowcontent"
              />
            </React.Suspense>
          )} */}

        <AnalyticsContext.Provider value={{ label: 'Article Below Content' }}>
          {layout && Array.isArray(layout?.content_footer?.blocks) && (
            <ArticleBlocks blocks={layout.content_footer.blocks} />
          )}
        </AnalyticsContext.Provider>

        {!articleData?.isHeadline && relatedArticles && <RelatedArticles nodes={relatedArticles} />}
      </ArticleContentWrapper>
    );
  },
);

export const NewArticleLayoutWrapper: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => <div className={classNames(styles.wrapper, className)}>{children}</div>;

export const LightBlueKeyPointss: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => <div className={classNames(styles.lightBlueKeyPoints, 'key-points-wrapper', className)}>{children}</div>;
